import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Button,
  Form,
  Input,
  Select,
  InputNumber,
  Row,
  Col,
  Typography,
  App,
  Space,
  Table,
} from 'antd';
import type { TableRowSelection } from 'antd/es/table/interface';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '@/services/api.service';
import {
  supplierInvoiceService,
  type UpdateSupplierInvoiceDTO,
  type UnclearedPoOverhead,
} from '@/services/supplierInvoice.service';
import { headerProjectLabel } from '@/utils/projectDisplay';
import ProcurementAttachmentsCard from '@/components/procurement/ProcurementAttachmentsCard';
import { DOCUMENT_ENTITY_TYPE_SUPPLIER_INVOICE } from '@/constants/document-entity-types';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';

const { Title } = Typography;

function unwrap<T>(res: any): T {
  const data = res?.data ?? res;
  return (data?.data ?? data) as T;
}

type OverheadPickerRow = UnclearedPoOverhead & { max_amount: number };

const EditSupplierInvoicePage: React.FC = () => {
  const { t } = useTranslation('procurement');
  const { t: tc } = useTranslation('common');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [selectedOverheadKeys, setSelectedOverheadKeys] = useState<React.Key[]>([]);
  const [overheadAmounts, setOverheadAmounts] = useState<Record<string, number>>({});
  const [overheadsSeedKey, setOverheadsSeedKey] = useState('');

  const { data: invoice, isLoading } = useQuery({
    queryKey: ['supplier-invoice', id],
    queryFn: () => supplierInvoiceService.getById(id!),
    enabled: !!id,
  });

  const existingOverheadById = useMemo(() => {
    const map = new Map<string, number>();
    for (const line of invoice?.lines ?? []) {
      if (line.po_overhead_id) {
        map.set(line.po_overhead_id, Number(line.amount) || 0);
      }
    }
    return map;
  }, [invoice?.lines]);

  const invoiceGrnIds = useMemo(() => {
    const ids = new Set<string>();
    if (invoice?.grn_id) ids.add(invoice.grn_id);
    if (invoice?.goodsReceiptNote?.id) ids.add(invoice.goodsReceiptNote.id);
    for (const line of invoice?.lines ?? []) {
      const nested =
        (line as any).goodsReceiptNoteItem?.goodsReceiptNote?.id ||
        (line as any).goodsReceiptNoteItem?.grn_id ||
        (line as any).goodsReceiptNote?.id;
      if (nested) ids.add(String(nested));
    }
    return [...ids];
  }, [invoice]);

  const { data: unclearedOverheads = [], isLoading: overheadsLoading } = useQuery({
    queryKey: [
      'supplier-invoice-uncleared-overheads-edit',
      id,
      invoice?.supplier_id,
      invoiceGrnIds.join(','),
      invoice?.po_id,
    ],
    queryFn: () =>
      supplierInvoiceService.getUnclearedOverheads({
        ...(invoiceGrnIds.length
          ? { grn_ids: invoiceGrnIds }
          : invoice?.po_id
            ? { po_id: invoice.po_id }
            : {}),
        supplier_id: invoice!.supplier_id,
      }),
    enabled: !!invoice?.supplier_id && (invoiceGrnIds.length > 0 || !!invoice?.po_id),
  });

  const overheadPickerRows: OverheadPickerRow[] = useMemo(() => {
    const byId = new Map<string, OverheadPickerRow>();
    for (const oh of unclearedOverheads) {
      const currentAmt = existingOverheadById.get(oh.id) ?? 0;
      const max = Math.round((Number(oh.remaining_amount) + currentAmt) * 100) / 100;
      byId.set(oh.id, {
        ...oh,
        remaining_amount: max,
        max_amount: max,
      });
    }
    for (const [ohId, currentAmt] of existingOverheadById) {
      if (byId.has(ohId)) continue;
      byId.set(ohId, {
        id: ohId,
        po_id: invoice?.po_id ?? '',
        po_number: invoice?.purchaseOrder?.po_number ?? null,
        line_number: 0,
        overhead_type: 'Overhead',
        description:
          invoice?.lines?.find((l) => l.po_overhead_id === ohId)?.item_description ?? null,
        amount: currentAmt,
        invoiced_amount: currentAmt,
        remaining_amount: currentAmt,
        max_amount: currentAmt,
        currency: invoice?.currency ?? 'AED',
      });
    }
    return [...byId.values()];
  }, [unclearedOverheads, existingOverheadById, invoice]);

  useEffect(() => {
    const seedKey = `${id}:${overheadPickerRows.map((r) => r.id).join(',')}:${[...existingOverheadById.keys()].join(',')}`;
    if (overheadsLoading) return;
    if (seedKey === overheadsSeedKey) return;
    setOverheadsSeedKey(seedKey);
    if (overheadPickerRows.length === 0) {
      setSelectedOverheadKeys([]);
      setOverheadAmounts({});
      return;
    }
    const preferExisting = existingOverheadById.size > 0;
    setSelectedOverheadKeys(
      preferExisting
        ? overheadPickerRows.filter((r) => existingOverheadById.has(r.id)).map((r) => r.id)
        : overheadPickerRows.map((r) => r.id)
    );
    setOverheadAmounts(
      Object.fromEntries(
        overheadPickerRows.map((r) => [
          r.id,
          existingOverheadById.get(r.id) ?? (Number(r.max_amount) || 0),
        ])
      )
    );
  }, [
    id,
    overheadPickerRows,
    existingOverheadById,
    overheadsLoading,
    overheadsSeedKey,
  ]);

  const { data: coaList = [] } = useQuery({
    queryKey: ['chart-of-accounts-ap'],
    queryFn: async () => {
      const res = await api.get('/finance/chart-of-accounts');
      const list = unwrap<any[]>(res) ?? [];
      return Array.isArray(list) ? list : [];
    },
  });
  const debitAccountOptions = useMemo(
    () => coaList.filter((a: any) => !a.is_group).map((a: any) => ({ value: a.id, label: `${a.account_code} – ${a.account_name}` })),
    [coaList]
  );

  React.useEffect(() => {
    if (invoice) {
      form.setFieldsValue({
        supplier_invoice_number: invoice.supplier_invoice_number,
        supplier_invoice_date: invoice.supplier_invoice_date?.slice(0, 10),
        received_date: invoice.received_date?.slice(0, 10),
        due_date: invoice.due_date?.slice(0, 10),
        payment_terms: invoice.payment_terms,
        debit_account_id: invoice.debit_account_id,
        department_id: invoice.department_id ?? undefined,
        notes: invoice.notes,
        discount_amount: invoice.discount_amount ?? 0,
        discount_account_id: invoice.discount_account_id ?? undefined,
        freight_charges: invoice.freight_charges ?? 0,
        other_charges: invoice.other_charges ?? 0,
      });
    }
  }, [invoice, form]);

  const discount = Form.useWatch('discount_amount', form) ?? 0;
  const showDiscountAccount = invoice?.invoice_type !== 'ServiceReceiptMatched' && Number(discount) > 0;

  const overheadTotal = useMemo(
    () =>
      selectedOverheadKeys.reduce<number>(
        (s, key) => s + (Number(overheadAmounts[String(key)]) || 0),
        0
      ),
    [selectedOverheadKeys, overheadAmounts]
  );

  const overheadRowSelection: TableRowSelection<OverheadPickerRow> = {
    selectedRowKeys: selectedOverheadKeys,
    onChange: (keys) => setSelectedOverheadKeys(keys),
  };

  const handleSubmit = async () => {
    if (!id || !invoice) return;
    try {
      await form.validateFields();
      const values = form.getFieldsValue();
      const goodsLines = (invoice.lines ?? [])
        .filter((l) => !!l.grn_item_id && !l.po_overhead_id && l.item_code !== 'OVERHEAD')
        .map((l) => ({
          grn_item_id: l.grn_item_id as string,
          quantity_billed: Number(l.quantity_billed),
          unit_price: Number(l.unit_price),
        }));
      const dto: UpdateSupplierInvoiceDTO = {
        supplier_invoice_number: values.supplier_invoice_number,
        supplier_invoice_date: values.supplier_invoice_date,
        received_date: values.received_date,
        due_date: values.due_date,
        payment_terms: values.payment_terms,
        debit_account_id: values.debit_account_id,
        department_id: values.department_id,
        notes: values.notes,
        discount_amount: values.discount_amount ?? 0,
        discount_account_id: Number(values.discount_amount ?? 0) > 0 ? values.discount_account_id ?? null : null,
        freight_charges: values.freight_charges ?? 0,
        other_charges: values.other_charges ?? 0,
        lines: goodsLines,
        overhead_lines: selectedOverheadKeys
          .map((key) => {
            const ohId = String(key);
            return { po_overhead_id: ohId, amount: Number(overheadAmounts[ohId]) || 0 };
          })
          .filter((o) => o.amount > 0),
      };
      setSubmitLoading(true);
      await supplierInvoiceService.update(id, dto);
      message.success(t('purchaseBillUpdated'));
      navigate(`/scm/procurement/supplier-invoices/${id}`);
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || t('failedToUpdate'));
    } finally {
      setSubmitLoading(false);
    }
  };

  if (!id) {
    navigate('/scm/procurement/supplier-invoices');
    return null;
  }
  if (isLoading || !invoice) {
    return (
      <div style={{ padding: 24 }}>
        <Card loading={isLoading}>{tc('loading')}</Card>
      </div>
    );
  }
  if (!['Draft', 'Submitted'].includes(invoice.invoice_status)) {
    message.warning(t('onlyDraftOrSubmittedEditable'));
    navigate(`/scm/procurement/supplier-invoices/${id}`);
    return null;
  }

  return (
    <div style={{ padding: 24 }}>
      <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate(`/scm/procurement/supplier-invoices/${id}`)} style={{ marginBottom: 16 }}>
        {t('backToBill')}
      </Button>
      <Title level={4} style={{ marginBottom: 24 }}>
        {t('editPurchaseBill')} {invoice.invoice_number}
      </Title>

      <Card style={{ marginBottom: 16 }}>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item label={t("project")}>
            <Input readOnly value={headerProjectLabel(invoice as any, invoice.lines)} />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="supplier_invoice_number" label={t("supplierInvoiceNumber")} rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="supplier_invoice_date" label={t("invoiceDate")} rules={[{ required: true }]}>
                <Input type="date" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="received_date" label={t("billDate")} rules={[{ required: true }]}>
                <Input type="date" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="due_date" label={t("dueDate")} rules={[{ required: true }]}>
                <Input type="date" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="debit_account_id" label={t("creditAccountAp")}>
                <Select placeholder={t('accountsPayable')} options={debitAccountOptions} allowClear showSearch optionFilterProp="label" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="department_id"
                label={t("department")}
                rules={[{ required: true, message: t('departmentRequired') }]}
              >
                <DepartmentSelect />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="payment_terms" label={t("paymentTerms")}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="discount_amount" label={t("discount")}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            {showDiscountAccount && (
              <Col xs={24} md={6}>
                <Form.Item
                  name="discount_account_id"
                  label={t("discountAccount")}
                  tooltip={t("discountAccountTooltip")}
                >
                  <Select
                    placeholder={t('discountAccountPlaceholder')}
                    options={debitAccountOptions}
                    allowClear
                    showSearch
                    optionFilterProp="label"
                  />
                </Form.Item>
              </Col>
            )}
            <Col xs={24} md={6}>
              <Form.Item label={t("vatFromPo")}>
                <Input value={invoice?.vat_amount != null ? Number(invoice.vat_amount).toFixed(2) : '0.00'} readOnly style={{ background: '#fafafa' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="freight_charges" label={t("freight")}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="other_charges" label={t("otherCharges")}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="notes" label={t("notes")}>
                <Input.TextArea rows={2} />
              </Form.Item>
            </Col>
          </Row>

          {(invoiceGrnIds.length > 0 || existingOverheadById.size > 0) && (
            <Card type="inner" title={t("poOverheadsToClear")} style={{ marginBottom: 16 }}>
              <p style={{ color: '#666', marginBottom: 12 }}>
                {t('poOverheadsEditHint')}
              </p>
              {overheadsLoading && overheadPickerRows.length === 0 ? (
                <p>{tc('loading')}</p>
              ) : overheadPickerRows.length === 0 ? (
                <p>{t('noPoOverheadsAvailable')}</p>
              ) : (
                <>
                  <Table
                    size="small"
                    rowKey="id"
                    rowSelection={overheadRowSelection}
                    dataSource={overheadPickerRows}
                    pagination={false}
                    columns={[
                      { title: t('purchaseOrderShort'), dataIndex: 'po_number', key: 'po_number', width: 110 },
                      { title: t('type'), dataIndex: 'overhead_type', key: 'overhead_type', width: 120 },
                      {
                        title: t('description'),
                        dataIndex: 'description',
                        key: 'description',
                        ellipsis: true,
                        render: (v: string | null) => v || '—',
                      },
                      {
                        title: t('max'),
                        dataIndex: 'max_amount',
                        key: 'max_amount',
                        width: 100,
                        align: 'right' as const,
                        render: (v: number) => Number(v).toFixed(2),
                      },
                      {
                        title: t('clearAmount'),
                        key: 'clear_amount',
                        width: 140,
                        render: (_: unknown, row: OverheadPickerRow) => (
                          <InputNumber
                            min={0.01}
                            max={row.max_amount}
                            value={overheadAmounts[row.id]}
                            disabled={!selectedOverheadKeys.includes(row.id)}
                            onChange={(v) =>
                              setOverheadAmounts((prev) => ({
                                ...prev,
                                [row.id]: Math.min(Number(v) || 0, row.max_amount),
                              }))
                            }
                            style={{ width: '100%' }}
                          />
                        ),
                      },
                    ]}
                  />
                  {overheadTotal > 0 && (
                    <div style={{ marginTop: 12, textAlign: 'right' }}>
                      <strong>{t('selectedOverheads')}</strong> {overheadTotal.toFixed(2)}
                    </div>
                  )}
                </>
              )}
            </Card>
          )}

          <Space>
            <Button type="primary" htmlType="submit" loading={submitLoading}>
              {tc('save')}
            </Button>
            <Button onClick={() => navigate(`/scm/procurement/supplier-invoices/${id}`)}>{tc('cancel')}</Button>
          </Space>
        </Form>
      </Card>

      <ProcurementAttachmentsCard
        entityType={DOCUMENT_ENTITY_TYPE_SUPPLIER_INVOICE}
        entityId={id}
        queryKeyPrefix={['documents', 'supplier-invoice']}
      />
    </div>
  );
};

export default EditSupplierInvoicePage;
